import { get, isNil, mapValues } from 'lodash';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';

import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { useIsMinWidth } from '@hooks/use-is-min-width';
import { useBrazeImpression } from '@hooks/use-braze-impression';
import { PageHeader } from '@components/page-header';
import { analytics } from '../../analytics';
import { Flex } from '../grid';
import { imageTransform } from '../../content/transforms';
import { OrderSettingsSummary } from '../order-settings-summary';
import { OversizedH1 } from '../typography/new';

const useBrazeOrderSettingsBanner = ({ heading, image, border, catering }) => {
  const { BrazeStore } = useRootStore();

  const { getLatestContentCardExtraByType } = BrazeStore;
  const cardMenuHeroImage = getLatestContentCardExtraByType(`menu-hero-image${catering ? '-catering' : ''}`);

  if (!cardMenuHeroImage) {
    return {
      heading,
      border,
      image,
    };
  }

  const brazeContent = mapValues({ heading, image, border }, (value, key) => get(cardMenuHeroImage, key, null));
  const brazeImage = get(imageTransform(brazeContent.image), 'url', image.src);

  return {
    ...brazeContent,
    brazeLogImpression: cardMenuHeroImage?.brazeLogImpression,
    image: brazeImage,
  };
};

const OrderSettingsBanner = ({ isRestaurantPreSet, isShortHeader, onClick, bannerHeading, disabled, catering }) => {
  const [shouldShowOrderSettings, setShouldShowOrderSettings] = useState(false);
  const { OrderingContextStore } = useRootStore();

  useEffect(() => {
    if ((isNil(OrderingContextStore.restaurant) && isRestaurantPreSet) || isShortHeader) {
      setShouldShowOrderSettings(false);
    } else {
      setShouldShowOrderSettings(true);
    }
  }, [OrderingContextStore.restaurant, isRestaurantPreSet, isShortHeader]);

  const isMinWidth = !useIsMinWidth('md');

  const backgroundImage = '/static/images/menu/menu_hero.jpg';
  const cateringBackgroundImage = '/static/images/menu/menu_hero_catering.jpg';

  const content = {
    heading: bannerHeading,
    image: catering ? cateringBackgroundImage : backgroundImage,
    catering,
  };

  const { heading, image, brazeLogImpression } = useBrazeOrderSettingsBanner(content);

  useBrazeImpression({ brazeLogImpression });

  return (
    <PageHeader minHeight={{ xs: '570px', md: '720px' }} heading={heading} image={image} headingLevel={OversizedH1}>
      {!isMinWidth && shouldShowOrderSettings && (
        <Flex mt="24px" justifyContent="center" width="100%" zIndex={2}>
          <OrderSettingsSummary
            disabled={disabled}
            onClick={() => {
              analytics.track(ANALYTICS_EVENTS.ORDER_SETTINGS_OPENED, {
                location: 'Banner',
              });
              onClick();
            }}
            isCompact={isMinWidth}
          />
        </Flex>
      )}
    </PageHeader>
  );
};
OrderSettingsBanner.propTypes = {
  isRestaurantPreSet: PropTypes.bool,
  isShortHeader: PropTypes.bool,
  onClick: PropTypes.func,
  bannerHeading: PropTypes.string,
  disabled: PropTypes.bool,
  catering: PropTypes.bool,
};

OrderSettingsBanner.defaultProps = {
  isRestaurantPreSet: false,
  isShortHeader: false,
  onClick: () => {},
  bannerHeading: ['Our', 'menu'],
  disabled: false,
  catering: false,
};

const ComposedOrderSettingsBanner = observer(OrderSettingsBanner);
export { ComposedOrderSettingsBanner as OrderSettingsBanner };
